<script lang="ts" setup>
  import type { StateMachineContext } from '~/models/StateMachineModels'
  import { SessionStore } from '~/stores/session'
  import { PlanType, WaypointResult } from '~/generated/api-clients-generated'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'

  const props = defineProps<{
    context: StateMachineContext
    presentation: string
  }>()

  const scenario = computed(() => (props.context?.config ?? 'add-needs') as PushToSaveScenarios)
  const { data } = await PushToSaveContent.use(scenario.value)

  const screen = computed(() => data.value?.screens?.successSignUp)

  const session = SessionStore.use()
  const profile = ProfileStore.use()

  const source = computed(() => props.context.data.get('source'))
  const displayCustomerEmail = computed(() => session.authBag.identifier?.replace('@', '<wbr />@'))
  const displayCustomerPhone = computed(() => formatPhone(profile.primaryContactPhone?.number))

  const { sendOutboundRequest } = OutboundLead.use()

  const { engage } = WaypointAutoPilot.use(WaypointResult.Next)
  const { push } = AppRouter.use()
  const { route } = AppRoute.use()
  const returnTo = ref(route.query.return_to?.toString())

  const onNext = async () => {
    if (returnTo.value) {
      await push(returnTo.value)
    } else {
      await engage()
    }
  }

  onMounted(async () => {
    if (
      (profile.primaryContactPhone || session.outboundLeadPhone) &&
      session.planType === PlanType.MAPD
    ) {
      await sendOutboundRequest(
        profile.emailAddress?.address ?? '',
        profile.primaryContactPhone?.number ?? session.outboundLeadPhone!,
        source.value
      )
    }
  })
</script>

<template>
  <section class="grid place-content-center place-items-center gap-5">
    <h2>{{ screen?.title }}</h2>

    <SiteSuccessAnimatedCheckmark />

    <p class="max-w-md text-balance text-center leading-snug">
      {{ screen?.text }}
    </p>

    <div
      class="grid gap-3 rounded-heading border border-border bg-gray-200 p-4 text-center font-normal"
    >
      <p class="leading-snug">
        Access your account anytime with:
        <span class="mt-1 block font-bold" v-html="displayCustomerEmail"></span>
      </p>
      <p v-if="displayCustomerPhone" class="border-t border-border pt-3 leading-snug">
        Reminders will go to:
        <span class="mt-1 block font-bold">{{ displayCustomerPhone }}</span>
      </p>
    </div>
    <HpButton
      v-if="props.presentation !== 'inline'"
      class="max-w-64"
      @click="context.data.get('onComplete')?.()"
    >
      Close
    </HpButton>
    <HpButton v-if="screen?.isNextShown" class="max-w-64" @click="onNext">
      {{ returnTo ? "Continue":screen?.nextText }}
    </HpButton>
  </section>
</template>
<style scoped></style>
